import Tooltip, { TooltipPresentationProps } from "@dana/Components/CustomTooltip/Tooltip";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StandardReturnsOrderRecepients from "@dana/Components/StandardReturnsOrderStatus/StandardReturnsOrderRecepients";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import CrossIcon from "@dana/Icons/CrossIcon";
import User from "@dana/Icons/User";
import XlsDownload from "@dana/Icons/XlsDownload";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import { OrderInfo, StandardReturnOrderDetails } from "@dana/Services/StandardReturnDetailsService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import exportStandardReturnOrder from "@dana/Store/Data/StandardReturnsStatus/Handlers/ExportStandardReturnOrder";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Styles Interfaces and Objects ---
export interface ReturnOrderDetailsStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    tooltipText?: TooltipPresentationProps;
}

export const ReturnOrderDetailsStyles: ReturnOrderDetailsStyles = {
    container: {
        css: css`
            overflow-x: auto;

            table {
                tr {
                    td {
                        white-space: break-spaces;
                    }
                }
            }
        `,
    },
    wrapper: {
        css: css`
            table {
                .address,
                address {
                    font-style: normal;
                }

                td.align-right {
                    text-align: right;
                }
            }

            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                column-gap: 24px;
            }

            .table-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                border: 1px solid ${themeBackgrounds.lightGray};
                margin-top: 32px;

                .grid-item {
                    display: flex;
                    flex-direction: column;

                    &:not(:last-child) {
                        border-right: 1px solid ${themeBackgrounds.lightGray};
                    }

                    .title {
                        background: ${themeBackgrounds.offWhite};
                        border-bottom: 1px solid ${themeBackgrounds.lightGray};
                        padding: 8px 4px;
                        font-size: 14px;
                        color: ${themeBackgrounds.darkGray};
                        font-weight: 600;
                    }

                    .grid-item-content {
                        padding: 8px 4px;
                        font-size: 14px;
                    }
                }

                @media only screen and (max-width: 768px) {
                    grid-template-columns: repeat(2, 1fr);
                }
            }
        `,
    },
    tooltipText: {
        mergeCss: true,
        iconProps: {
            src: User,
        },
        cssOverrides: {
            tooltipBody: css`
                font-weight: ${fontWeights.regular};
            `,
            tooltipContainer: css`
                width: max-content;
            `,
            tooltipCloseButton: css`
                top: 8px;
                right: 8px;

                [class*="IconWrapper"] {
                    svg {
                        path {
                            fill: ${themeColors.primary.richBlack};
                        }
                    }
                }
            `,
        },
    },
};

const styles = ReturnOrderDetailsStyles;
const mapStateToProps = (state: ApplicationStateDana) => ({
    language: state.context.session.language,

    standardReturnsDetailsState: state.standardReturnsDetailsState,
});

const mapDispatchToProps = {
    exportStandardReturnOrder,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
// --- Component Definition ---
const ReturnOrderDetails: FC<Props> = ({ standardReturnsDetailsState, exportStandardReturnOrder }) => {
    const [order, setOrder] = useState<StandardReturnOrderDetails>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    useEffect(() => {
        setOrder(standardReturnsDetailsState?.orderModel);
        setIsLoading(standardReturnsDetailsState?.isLoading);
    }, [standardReturnsDetailsState.orderModel]);
    const exportStandardOrderHandler = () => {
        exportStandardReturnOrder({});
    };
    const modalContentRef = useRef<HTMLDivElement>(null);
    const returnOrderTooltipContent = (
        <StyledWrapper className="text-left">
            <Typography as="p" className="font-semibold">
                {translate("Created By")}
            </Typography>
            <br />
            <Typography as="p">
                <Typography className="font-semibold">{translate("User Type")}:</Typography> I
            </Typography>
            <Typography as="p">
                <Typography className="font-semibold">{translate("User name")}:</Typography> {order?.firstName}{" "}
                {order?.lastName}
            </Typography>
            <Typography as="p">
                <Typography className="font-semibold">{translate("User ID")}:</Typography> {order?.userId}
            </Typography>
            <Typography as="p">
                <Typography className="font-semibold">{translate("Email ID")}:</Typography> {order?.userEmail}
            </Typography>
        </StyledWrapper>
    );
    if (isLoading && !order) {
        return <LoadingSpinner></LoadingSpinner>;
    }
    return (
        <>
            {order && (
                <StyledWrapper ref={modalContentRef}>
                    <StyledWrapper {...styles.wrapper}>
                        <div className="invoice-actions text-right">
                            {order && (
                                <StandardReturnsOrderRecepients
                                    orderType="StandardReturn"
                                    orderData={order}
                                    contentRef={modalContentRef}
                                />
                            )}

                            <PrintButton label={translate("Print Order details")} targetRef={modalContentRef} />

                            {order?.orderInfo && (
                                <Button variant="tertiary" onClick={() => exportStandardOrderHandler()}>
                                    <Icon
                                        src={XlsDownload}
                                        title="Export to Excel"
                                        color={themeBackgrounds.darkGray}
                                        size={24}
                                    />
                                </Button>
                            )}
                        </div>
                        <StyledWrapper {...styles.container}>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <th>{translate(`SOLD TO: ${order?.mailTo}`)}</th>
                                        <th>{translate(`SHIPPING FROM: ${order?.shipTo}`)}</th>
                                        <th>{translate(`BILL TO: ${order?.billTo}`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="address">
                                                {order?.billName}
                                                <br />
                                                {order?.billAddress1}
                                                <br />
                                                {order?.billCity} {order?.billState} {order?.billZipcode}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="address">
                                                {order?.shipName}
                                                <br />
                                                {order?.shipAddress1}
                                                <br />
                                                {order?.shipCity} {order?.shipState} {order?.shipZipcode}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="address">
                                                {order?.billName}
                                                <br />
                                                {order?.billAddress1}
                                                <br />
                                                {order?.billCity} {order?.billState} {order?.billZipcode}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </StyledTable>
                        </StyledWrapper>
                        <StyledWrapper className="table-grid">
                            <StyledWrapper className="grid-item">
                                <Typography className="title">{translate("ReturnProcess No")}</Typography>
                                <Typography className="grid-item-content">{order?.processNumber}</Typography>
                            </StyledWrapper>
                            <StyledWrapper className="grid-item">
                                <Typography className="title">{translate("Return Order No.")}</Typography>
                                <Typography className="grid-item-content">
                                    {order?.documentNumber}{" "}
                                    <Tooltip
                                        {...styles.tooltipText}
                                        showCloseButton
                                        closeButtonAriaLabel={translate("Dismiss tooltip")}
                                        closeIconProps={{ src: CrossIcon, size: 14 }}
                                        tooltipContent={returnOrderTooltipContent}
                                    />
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper className="grid-item">
                                <Typography className="title">{translate("Primary PO No.")}</Typography>
                                <Typography className="grid-item-content">{order?.poNumber}</Typography>
                            </StyledWrapper>
                            <StyledWrapper className="grid-item">
                                <Typography className="title">{translate("Order Type")}</Typography>
                                <Typography className="grid-item-content">{translate("Standard Return")}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.container}>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <th>{translate("Part No")}</th>
                                        <th>{translate("Description")}</th>
                                        <th>
                                            {translate("Qty. Requested")} <br /> {translate("for Returns")}
                                        </th>
                                        <th>{translate(`Unit Price(${order?.currency})`)}</th>
                                        <th>{translate(`Unit Core Deposit(${order?.currency})`)}</th>
                                        <th>{translate(`Ext. Price(${order?.currency})`)}</th>
                                        <th>{translate("Weight (LB)")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order?.orderInfo.map((orderline: OrderInfo) => {
                                        return (
                                            <tr key={orderline.partNumber}>
                                                <td>{orderline.partNumber}</td>
                                                <td>{orderline.partDescription}</td>
                                                <td>{orderline.quantityOrdered}</td>
                                                <td className="align-right">
                                                    {formatDecimal(orderline.pricePerUnit)}</td>
                                                <td className="align-right">
                                                    {formatDecimal(orderline.extendedAmount)}</td>
                                                <td className="align-right">
                                                    {formatDecimal(
                                                        orderline.pricePerUnit * orderline.quantityOrdered +
                                                        orderline.extendedAmount
                                                    )}
                                                </td>
                                                <td className="align-right">
                                                    {formatDecimal(orderline.weight * orderline.quantityOrdered)}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {(() => {
                                        if (!order?.orderInfo) {
                                            return null;
                                        }

                                        const totalQuantity = order.orderInfo.reduce(
                                            (sum, item) => sum + (item.quantityOrdered || 0),
                                            0,
                                        );
                                        const totalNetAmount = order.orderInfo.reduce(
                                            (sum, item) =>
                                                sum +
                                                (item.pricePerUnit * item.quantityOrdered + item.extendedAmount || 0),
                                            0,
                                        );
                                        const totalWeight = order.orderInfo.reduce(
                                            (sum, item) => sum + (item.weight * item.quantityOrdered || 0),
                                            0,
                                        );

                                        return (
                                            <tr>
                                                <td colSpan={2} className="align-right">
                                                    <strong>{translate("Total")}</strong>
                                                </td>
                                                <td>{totalQuantity}</td>
                                                <td colSpan={2}></td>
                                                <td className="align-right">
                                                    <strong>{formatDecimal(totalNetAmount)}</strong>
                                                </td>
                                                <td className="align-right">
                                                    <strong>{formatDecimal(totalWeight)}</strong>
                                                </td>
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </StyledTable>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReturnOrderDetails);
